import { useEffect, useState, useCallback, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  applyNodeChanges,
  applyEdgeChanges,
  addEdge,
  Handle,
  Position,
} from "reactflow";
import "reactflow/dist/style.css";
import "./RoadmapView.css";
import api from "../../api/api";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import ProjectModal from "../../components/ProjectModal/ProjectModal";
import SkillModal   from "../../components/SkillModal/SkillModal";
import BackButton   from "../../components/BackButton/BackButton";

/* ============================================================
   § Helpers
   ============================================================ */
const extractVideoId = (url = "") => {
  const m = url.match(/(?:v=|youtu\.be\/)([^&?/]+)/);
  return m ? m[1] : null;
};

const getThumbFromResources = (item) => {
  const sources = item.resources || item.playlists || [];

  for (const r of sources) {
    const thumb = r?.course?.thumbnail;
    if (thumb) return thumb;

    const vid = extractVideoId(r.url || "");
    if (vid) return `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
  }

  return null;
};

const typeConfig = {
  skill:   { icon: "🎯", color: "#2563eb", gradient: "linear-gradient(135deg,#2563eb,#3b82f6)", label: "Skill"   },
  project: { icon: "🚀", color: "#7c3aed", gradient: "linear-gradient(135deg,#7c3aed,#8b5cf6)", label: "Project" },
};

// Status dot config for project nodes
const statusConfig = {
  planned:     { dot: "#94a3b8", label: "Planned"     },
  in_progress: { dot: "#f59e0b", label: "In Progress" },
  completed:   { dot: "#10b981", label: "Completed"   },
};

/* ============================================================
   § Custom Node — redesigned with numbering, status, clean card
   ============================================================ */
function RoadmapNode({ data }) {
  const navigate = useNavigate();
  const cfg    = typeConfig[data.type] || typeConfig.skill;
  const thumb  = data.thumbnail;
  const num    = data.index + 1;
  const status = data.type === "project"
    ? (statusConfig[data.existing?.status] || statusConfig.planned)
    : null;

  const tech = data.type === "project"
    ? (data.existing?.techStack || []).slice(0, 3)
    : [];

  const resourceCount = data.type === "skill"
    ? (data.existing?.resources || data.existing?.playlists || []).filter(r => r.url?.trim()).length
    : 0;

  // Check if any resource was deleted by the user
  const hasDetached = data.type === "skill" &&
    (data.existing?.resources || data.existing?.playlists || []).some(r => r.detached);

  return (
    <div
      className={`rn-node rn-node--${data.type} ${data.editMode ? "rn-node--editable" : ""} ${hasDetached ? "rn-node--detached" : ""}`}
      style={{ "--node-color": cfg.color, "--node-gradient": cfg.gradient }}
    >
      <Handle type="target" position={Position.Top}    className="rn-handle" />
      <Handle type="source" position={Position.Bottom} className="rn-handle" />

      <div className="rn-step-num" style={{ background: cfg.gradient }}>
        {num}
      </div>

      {thumb ? (
        <div className="rn-thumb-wrap">
          <img src={thumb} alt="" className="rn-thumb" />
          <div className="rn-thumb-overlay" />
          <div className="rn-type-chip" style={{ background: cfg.gradient }}>
            {cfg.icon} {cfg.label}
          </div>
        </div>
      ) : (
        <div className="rn-no-thumb" style={{ background: cfg.gradient + "1a" }}>
          <div className="rn-no-thumb-icon" style={{ background: cfg.gradient }}>
            {cfg.icon}
          </div>
          <div className="rn-type-chip rn-type-chip--solo" style={{ background: cfg.gradient }}>
            {cfg.label}
          </div>
        </div>
      )}

      <div className="rn-body">
        <div className="rn-title" title={data.label}>{data.label}</div>

        {data.type === "project" && (
          <div className="rn-meta">
            {status && (
              <span className="rn-status">
                <span className="rn-status-dot" style={{ background: status.dot }} />
                {status.label}
              </span>
            )}
            {tech.length > 0 && (
              <div className="rn-tech-row">
                {tech.map((t) => (
                  <span key={t} className="rn-tech-chip">{t}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {data.type === "skill" && resourceCount > 0 && (
          <div className="rn-resource-count">
            📚 {resourceCount} resource{resourceCount !== 1 ? "s" : ""}
          </div>
        )}

        {/* Detached resource warning — course was deleted */}
        {hasDetached && !data.editMode && (
          <div className="rn-detached-warning">
            ⚠ Course removed — re-add in edit mode
          </div>
        )}

        {/* View resources button — skill nodes, view mode only */}
        {data.type === "skill" && !data.editMode && data.roadmapId && resourceCount > 0 && (
          <button
            className="rn-view-skill-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/skill/${data.roadmapId}?itemId=${data.existing._id}`);
            }}
          >
            View Resources →
          </button>
        )}

        {/* View project button — view mode only */}
        {data.type === "project" && !data.editMode && data.roadmapId && (
          <button
            className="rn-view-project-btn"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/project/${data.roadmapId}?itemId=${data.existing._id}`);
            }}
          >
            View Project →
          </button>
        )}

        {data.editMode && (
          <div className="rn-edit-hint">✏ tap to edit</div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   § Main Component
   ============================================================ */
function RoadmapView() {
  const { id }              = useParams();
  const [searchParams]      = useSearchParams();
  const navigate            = useNavigate();

  const [roadmap,  setRoadmap]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [nodes,    setNodes]    = useState([]);
  const [edges,    setEdges]    = useState([]);
  const [continueMsg, setContinueMsg] = useState(""); // feedback when nothing to continue

  // Open edit mode if ?edit=true (coming back from RoadmapAddItem)
  const [editMode, setEditMode] = useState(searchParams.get("edit") === "true");

  const [selectedNode,       setSelectedNode]       = useState(null);
  const [isModalOpen,        setIsModalOpen]        = useState(false);
  const [selectedEdge,       setSelectedEdge]       = useState(null);
  const [showDeleteConfirm,  setShowDeleteConfirm]  = useState(false);
  const [confirmText,        setConfirmText]        = useState("");
  const [deleting,           setDeleting]           = useState(false);

  // Register custom node type — must be stable (useMemo)
  const nodeTypes = useMemo(() => ({ roadmapNode: RoadmapNode }), []);

  /* ── Fetch ─────────────────────────────────────────────── */
  useEffect(() => {
    const fetchRoadmap = async () => {
      try {
        const res  = await api.get(`/roadmap/${id}`);
        const data = res.data.roadmap;
        setRoadmap(data);
        buildGraph(data);
      } catch (err) {
        if (err.response?.status === 404) setRoadmap(false);
        else console.error("Roadmap fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchRoadmap();
    // eslint-disable-next-line
  }, [id]);

  // Re-fetch when user returns to this tab (e.g. deleted course on dashboard)
  // Guard: only re-fetch if we have a valid roadmap loaded and aren't deleting
  useEffect(() => {
    let isDeleting = false;
    const onVisible = () => {
      if (document.visibilityState === "visible" && !isDeleting) {
        api.get(`/roadmap/${id}`).then(res => {
          if (res.data.roadmap) {
            setRoadmap(res.data.roadmap);
            buildGraph(res.data.roadmap);
          }
        }).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      isDeleting = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line
  }, [id]);

  // Sync editMode into nodes so the custom node knows whether to show "click to edit"
  useEffect(() => {
    setNodes((prev) =>
      prev.map((n) => ({
        ...n,
        data: { ...n.data, editMode },   // roadmapId + index already in data, untouched
      }))
    );
  }, [editMode]);

  const buildGraph = (data) => {
    const allZero =
      data.items.length > 0 &&
      data.items.every(
        (item) => !item.position || (item.position.x === 0 && item.position.y === 0)
      );

    const generatedNodes = data.items.map((item, index) => {
      const autoPos = {
        x: (index % 3) * 320,
        y: Math.floor(index / 3) * 240,
      };
      return {
        id:   item._id,
        type: "roadmapNode",
        position: allZero ? autoPos : (item.position || autoPos),
        data: {
          label:     item.name,
          type:      item.type,
          index,
          thumbnail: getThumbFromResources(item),
          existing:  item,
          editMode,
          roadmapId: data._id,  // ← needed for project page navigation
        },
      };
    });

    const generatedEdges = (data.edges || []).map((e, i) => ({
      id:        `e-${i}`,
      source:    e.source,
      target:    e.target,
      animated:  false,
      markerEnd: { type: "arrowclosed" },
      style:     { stroke: "#94a3b8", strokeWidth: 2 },
    }));

    setNodes(generatedNodes);
    setEdges(generatedEdges);
  };

  /* ── Node/Edge change handlers ─────────────────────────── */
  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes) => {
      if (!editMode) return;
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [editMode]
  );

  /* ── Edge click ─────────────────────────────────────────── */
  const handleEdgeClick = useCallback(
    (_, edge) => {
      if (!editMode) return;
      setSelectedEdge(edge.id);
    },
    [editMode]
  );

  const handleDeleteEdge = () => {
    if (!selectedEdge) return;
    setEdges((eds) => eds.filter((e) => e.id !== selectedEdge));
    setSelectedEdge(null);
  };

  /* ── Keyboard delete ────────────────────────────────────── */
  const handleKeyDown = useCallback(
    (e) => {
      if (!editMode) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        setEdges((eds) => eds.filter((e) => !e.selected));
        setSelectedEdge(null);
      }
    },
    [editMode]
  );

  /* ── Connection validation ──────────────────────────────── */
  const isValidConnection = useCallback(
    ({ source, target }) => {
      if (source === target) return false;

      if (edges.some((e) => e.source === source)) return false;
      if (edges.some((e) => e.target === target)) return false;

      // cycle detection
      let cur = target;

      while (true) {
        let next = null;

        for (let i = 0; i < edges.length; i++) {
          if (edges[i].source === cur) {
            next = edges[i];
            break;
          }
        }

        if (!next) return true;
        if (next.target === source) return false;

        cur = next.target;
      }
    },
    [edges]
  );

  const handleConnect = useCallback(
    (connection) => {
      if (!editMode) return;
      if (!isValidConnection(connection)) return;
      setEdges((eds) =>
        addEdge(
          { ...connection, animated: true, markerEnd: { type: "arrowclosed" }, style: { stroke: "#2563eb", strokeWidth: 2 } },
          eds
        )
      );
    },
    [editMode, isValidConnection]
  );

  /* ── Node click ─────────────────────────────────────────── */
  const handleNodeClick = useCallback(
    (_, node) => {
      if (!editMode)  return;
      if (node.dragging) return;
      setSelectedNode(node);
      setIsModalOpen(true);
    },
    [editMode]
  );

  /* ── Save layout ────────────────────────────────────────── */
  const saveLayout = async () => {
    if (!roadmap) return;
    try {
      await api.patch(`/roadmap/${roadmap._id}/layout`, {
        positions: nodes.map((n) => ({
          itemId: n.id,
          x: n.position.x,
          y: n.position.y,
        })),
      });
      await api.patch(`/roadmap/${roadmap._id}/edges`, {
        edges: edges.map((e) => ({ source: e.source, target: e.target })),
      });
      setEditMode(false);
      setSelectedEdge(null);
      // clear ?edit param from URL without re-navigating
      window.history.replaceState({}, "", `/roadmap/${id}`);
    } catch (err) {
      console.error("Save failed:", err);
    }
  };

  /* ── Modal save (edit existing node) ───────────────────── */
  const handleModalSave = async (updatedData) => {
    if (!selectedNode || !roadmap) return;
    try {
      const res = await api.patch(
        `/roadmap/${roadmap._id}/items/${selectedNode.id}`,
        updatedData
      );
      const updatedItem = res.data.item;
      setNodes((prev) =>
        prev.map((n) =>
          n.id === updatedItem._id
            ? {
                ...n,
                data: {
                  ...n.data,
                  label:     updatedItem.name,
                  thumbnail: getThumbFromResources(updatedItem),
                  existing:  updatedItem,
                },
              }
            : n
        )
      );
      setIsModalOpen(false);
      setSelectedNode(null);
    } catch (err) {
      console.error("Item update failed:", err);
    }
  };

  /* ── Delete roadmap ─────────────────────────────────────── */
  const handleDeleteRoadmap = async () => {
    if (!roadmap || confirmText !== roadmap.title) return;

    const roadmapId = roadmap._id?.toString();
    if (!roadmapId) {
      console.error("roadmap._id is missing", roadmap);
      return;
    }

    try {
      setDeleting(true);
      // Navigate away FIRST so the visibilitychange listener
      // doesn't fire a re-fetch for an already-deleted roadmap
      navigate("/roadmaps");
      await api.delete(`/roadmap/${roadmapId}`);
    } catch (err) {
      console.error("Delete failed:", err);
      setDeleting(false);
    }
  };

  /* ── Loading / empty states ─────────────────────────────── */
  if (loading) {
    return (
      <div className="rv-loading">
        <div className="rv-spinner" />
        <p>Loading roadmap…</p>
      </div>
    );
  }

  if (roadmap === false) {
    return (
      <div className="rv-empty-state">
        <div className="rv-empty-icon">🗺️</div>
        <h2>No Roadmap Found</h2>
        <p>Create your first learning roadmap to visualise your journey.</p>
        <button className="rv-btn rv-btn--primary" onClick={() => navigate("/roadmap/create")}>
          Create Roadmap →
        </button>
      </div>
    );
  }

  /* ── Main render ────────────────────────────────────────── */
  return (
    <div className="rv-wrapper" tabIndex={0} onKeyDown={handleKeyDown}>

      {/* Topbar */}
      <div className="rv-topbar">
        <div className="rv-topbar-left">
          <BackButton to="/roadmaps" label="Roadmaps" />
          <h2 className="rv-title">{roadmap.title}</h2>
          {editMode && (
            <span className="rv-edit-badge">✏️ Edit Mode</span>
          )}
        </div>

        <div className="rv-topbar-actions">
          {/* Continue Learning — only in view mode */}
          {!editMode && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px" }}>
              <button
                className="rv-btn rv-btn--continue"
                onClick={async () => {
                  setContinueMsg("");
                  try {
                    const res = await api.get(`/roadmap/current-course/${roadmap._id}`);
                    if (res.data.type === "course" && res.data.courseId) {
                      navigate(`/course/${res.data.courseId}`);
                    } else if (res.data.type === "project" && res.data.projectId) {
                      navigate(`/project/${roadmap._id}`);
                    } else {
                      // type is null — all steps done or all courses detached
                      setContinueMsg("No active step — check your roadmap for detached courses.");
                      setTimeout(() => setContinueMsg(""), 4000);
                    }
                  } catch {
                    setContinueMsg("Could not load current step.");
                    setTimeout(() => setContinueMsg(""), 4000);
                  }
                }}
              >
                ▶ Continue Learning
              </button>
              {continueMsg && (
                <span style={{ fontSize: "11px", color: "var(--bad, #ef4444)", maxWidth: "220px", textAlign: "right" }}>
                  {continueMsg}
                </span>
              )}
            </div>
          )}

          {/* Edit mode actions */}
          {editMode && (
            <>
              <button
                className="rv-btn rv-btn--secondary"
                onClick={() => navigate(`/roadmap/add-item/${roadmap._id}`)}
              >
                + Add Item
              </button>
              <button className="rv-btn rv-btn--primary" onClick={saveLayout}>
                ✓ Save Layout
              </button>
            </>
          )}

          <button
            className={`rv-btn ${editMode ? "rv-btn--ghost" : "rv-btn--secondary"}`}
            onClick={() => {
              setEditMode(!editMode);
              if (editMode) {
                // clear edit param on cancel
                window.history.replaceState({}, "", `/roadmap/${id}`);
              }
            }}
          >
            {editMode ? "Cancel" : "✏️ Edit"}
          </button>

          {editMode && selectedEdge && (
            <button className="rv-btn rv-btn--danger-sm" onClick={handleDeleteEdge}>
              Delete Connection
            </button>
          )}

          <button
            className="rv-btn rv-btn--danger"
            onClick={() => setShowDeleteConfirm(true)}
          >
            🗑
          </button>
        </div>
      </div>

      {/* Flow canvas */}
      <div className="rv-flow">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgeClick={handleEdgeClick}
          onNodeClick={handleNodeClick}
          onConnect={handleConnect}
          nodesDraggable={editMode}
          nodesConnectable={editMode}
          fitView
          fitViewOptions={{ padding: 0.6, maxZoom: 0.85 }}
          minZoom={0.2}
          maxZoom={2}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
        >
          <MiniMap
            nodeColor={(n) => typeConfig[n.data?.type]?.color || "#94a3b8"}
            style={{ background: "var(--bg-card, #fff)", border: "1px solid var(--border)" }}
          />
          <Controls />
          <Background gap={20} color="var(--border, rgba(0,0,0,0.06))" />
        </ReactFlow>
      </div>

      {/* ── Skill Modal ─────────────────────────────────── */}
      {isModalOpen && selectedNode?.data?.type === "skill" && (
        <>
          <div className="modal-backdrop" onClick={() => setIsModalOpen(false)} />
          <div className="rv-modal-wrapper">
            <SkillModal
              existing={selectedNode.data.existing}
              onClose={() => { setIsModalOpen(false); setSelectedNode(null); }}
              onSave={handleModalSave}
            />
          </div>
        </>
      )}

      {/* ── Project Modal ───────────────────────────────── */}
      {isModalOpen && selectedNode?.data?.type === "project" && (
        <>
          <div className="modal-backdrop" onClick={() => setIsModalOpen(false)} />
          <div className="rv-modal-wrapper">
            <ProjectModal
              existing={selectedNode.data.existing}
              onClose={() => { setIsModalOpen(false); setSelectedNode(null); }}
              onSave={handleModalSave}
            />
          </div>
        </>
      )}

      {/* ── Delete confirm ──────────────────────────────── */}
      {showDeleteConfirm && (
        <>
          <div className="modal-backdrop" onClick={() => setShowDeleteConfirm(false)} />
          <div className="rv-modal-wrapper">
            <div className="rv-delete-modal">
              <h3>Delete Roadmap</h3>
              <p>
                This cannot be undone. Type <strong>{roadmap.title}</strong> to confirm.
              </p>
              <input
                className="rv-delete-input"
                placeholder="Enter roadmap title"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
              />
              <div className="rv-delete-actions">
                <button
                  className="rv-btn rv-btn--danger"
                  disabled={confirmText !== roadmap.title || deleting}
                  onClick={handleDeleteRoadmap}
                >
                  {deleting ? "Deleting…" : "Confirm Delete"}
                </button>
                <button
                  className="rv-btn rv-btn--ghost"
                  onClick={() => { setShowDeleteConfirm(false); setConfirmText(""); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export default RoadmapView;